import { Box, Chip, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { splitByGaps } from '../contentTypes'
import type { DropdownGapsContent, GapsContent, SentenceBuilderContent } from '../contentTypes'
import { DragPickerProvider } from './dnd'
import { sourceProps, targetProps, useDragPicker } from './dragPicker'

/**
 * Рендереры типов с пропусками. Ответ собирается в форму, которую ждёт чекер:
 * `{ gaps: { "1": "…" }, hints: ["1"] }` — ключ пропуска и есть номер
 * плейсхолдера в тексте.
 */

export type RendererProps<T> = {
  content: T
  answers: Record<string, unknown>
  onChange: (answers: Record<string, unknown>) => void
  readOnly?: boolean
}

function gapsOf(answers: Record<string, unknown>): Record<string, string> {
  const value = answers.gaps
  return typeof value === 'object' && value !== null ? (value as Record<string, string>) : {}
}

export function ClozeRenderer({ content, answers, onChange, readOnly }: RendererProps<GapsContent>) {
  const gaps = gapsOf(answers)

  return (
    <Typography component="div" sx={{ lineHeight: 2.4 }}>
      {splitByGaps(content.text).map((part, index) =>
        part.kind === 'text' ? (
          <span key={index}>{part.value}</span>
        ) : (
          <TextField
            key={index}
            size="small"
            variant="standard"
            sx={{ mx: 0.5, width: 140 }}
            value={gaps[String(part.index)] ?? ''}
            disabled={readOnly}
            onChange={(event) =>
              onChange({ ...answers, gaps: { ...gaps, [String(part.index)]: event.target.value } })
            }
          />
        ),
      )}
    </Typography>
  )
}

function DragGapsBody({ content, answers, onChange, readOnly }: RendererProps<GapsContent>) {
  const { t } = useTranslation()
  const picker = useDragPicker()
  const gaps = gapsOf(answers)
  const used = Object.values(gaps)

  const place = (gapKey: string, word: string) => {
    onChange({ ...answers, gaps: { ...gaps, [gapKey]: word } })
  }

  return (
    <Stack spacing={3}>
      <Typography component="div" sx={{ lineHeight: 2.6 }}>
        {splitByGaps(content.text).map((part, index) => {
          if (part.kind === 'text') return <span key={index}>{part.value}</span>

          const key = String(part.index)
          const value = gaps[key]

          return (
            <Box
              key={index}
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 110,
                mx: 0.5,
                px: 1,
                py: 0.5,
                borderBottom: 2,
                borderColor: value ? 'primary.main' : 'divider',
                cursor: readOnly ? 'default' : 'pointer',
              }}
              {...targetProps((word) => place(key, word), picker, readOnly)}
              onDoubleClick={() => {
                if (readOnly) return
                const next = { ...gaps }
                delete next[key]
                onChange({ ...answers, gaps: next })
              }}
            >
              {value || ' '}
            </Box>
          )
        })}
      </Typography>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('exercises.run.wordBank')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(content.bank ?? []).map((word, index) => (
            <Chip
              key={`${word}-${index}`}
              label={word}
              color={picker.isPicked(word) ? 'primary' : 'default'}
              variant={used.includes(word) ? 'outlined' : 'filled'}
              disabled={readOnly}
              {...sourceProps(word, picker, readOnly)}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('exercises.run.dragHint')}
        </Typography>
      </Box>
    </Stack>
  )
}

export function DragGapsRenderer(props: RendererProps<GapsContent>) {
  return (
    <DragPickerProvider>
      <DragGapsBody {...props} />
    </DragPickerProvider>
  )
}

export function DropdownGapsRenderer({ content, answers, onChange, readOnly }: RendererProps<DropdownGapsContent>) {
  const gaps = gapsOf(answers)

  return (
    <Typography component="div" sx={{ lineHeight: 2.6 }}>
      {splitByGaps(content.text).map((part, index) => {
        if (part.kind === 'text') return <span key={index}>{part.value}</span>

        const key = String(part.index)
        // Позиция пропуска в тексте и есть его позиция в списке gaps
        const gap = content.gaps[part.index - 1]

        return (
          <Select
            key={index}
            size="small"
            displayEmpty
            sx={{ mx: 0.5, minWidth: 140 }}
            value={gaps[key] ?? ''}
            disabled={readOnly}
            onChange={(event) => onChange({ ...answers, gaps: { ...gaps, [key]: String(event.target.value) } })}
          >
            <MenuItem value="">—</MenuItem>
            {(gap?.options ?? []).map((option, optionIndex) => (
              <MenuItem key={optionIndex} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        )
      })}
    </Typography>
  )
}

function SentenceBuilderBody({ content, answers, onChange, readOnly }: RendererProps<SentenceBuilderContent>) {
  const { t } = useTranslation()
  const order = Array.isArray(answers.order) ? (answers.order as number[]) : []

  const append = (index: number) => {
    if (order.includes(index)) return
    onChange({ ...answers, order: [...order, index] })
  }

  const removeAt = (position: number) => {
    onChange({ ...answers, order: order.filter((_, i) => i !== position) })
  }

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          minHeight: 64,
          p: 2,
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {order.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('exercises.run.buildHere')}
            </Typography>
          )}
          {order.map((wordIndex, position) => (
            <Chip
              key={`${wordIndex}-${position}`}
              label={content.words[wordIndex]}
              color="primary"
              disabled={readOnly}
              onDelete={readOnly ? undefined : () => removeAt(position)}
            />
          ))}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {content.words.map((word, index) => (
          <Chip
            key={`${word}-${index}`}
            label={word}
            variant={order.includes(index) ? 'outlined' : 'filled'}
            disabled={readOnly || order.includes(index)}
            onClick={() => append(index)}
          />
        ))}
      </Stack>
    </Stack>
  )
}

export function SentenceBuilderRenderer(props: RendererProps<SentenceBuilderContent>) {
  return (
    <DragPickerProvider>
      <SentenceBuilderBody {...props} />
    </DragPickerProvider>
  )
}
